package co.maytes.standupbot.dashboard.pages.app.standups.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.FormRow
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.SectionCard
import co.maytes.standupbot.dashboard.components.widgets.Switch
import co.maytes.standupbot.dashboard.features.standups.ReviewSettings
import co.maytes.standupbot.dashboard.features.standups.ScheduleBuilder
import co.maytes.standupbot.dashboard.features.standups.ScheduleSpec
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.ReviewModeDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@Composable
internal fun ScheduleTab(
    standup: StandupDto,
    workspaceId: String?,
    inFlight: Boolean,
    onSave: (ScheduleSpec) -> Unit,
    onSaveHolidays: (UpdateStandupRequestDto) -> Unit,
) {
    var spec by remember(standup.id, standup.scheduleCron, standup.timezone) {
        mutableStateOf(ScheduleSpec.parse(standup.scheduleCron, standup.timezone))
    }
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
        SectionCard(
            title = "Schedule",
            description = "When this standup fires, in the team's timezone.",
        ) {
            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
                ScheduleBuilder(value = spec, onChange = { spec = it })
                Row(
                    modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Button(
                        label = "Reset",
                        variant = ButtonVariant.Ghost,
                        enabled = !inFlight,
                        onClick = {
                            spec = ScheduleSpec.parse(standup.scheduleCron, standup.timezone)
                        },
                    )
                    Button(
                        label = if (inFlight) "Saving…" else "Save schedule",
                        variant = ButtonVariant.Primary,
                        enabled = !inFlight && spec.firesAtLeastOnce,
                        onClick = { onSave(spec) },
                    )
                }
            }
        }
        ReviewCard(
            standup = standup,
            inFlight = inFlight,
            onSave = onSaveHolidays,
        )
        MeetingLinkCard(
            standup = standup,
            inFlight = inFlight,
            onSave = onSaveHolidays,
        )
        HolidaysCard(
            standup = standup,
            workspaceId = workspaceId,
            inFlight = inFlight,
            onSave = onSaveHolidays,
        )
        ActivityWindowCard(
            standup = standup,
            inFlight = inFlight,
            onSave = onSaveHolidays,
        )
        AtRiskCard(
            standup = standup,
            inFlight = inFlight,
            onSave = onSaveHolidays,
        )
    }
}

@Composable
private fun ReviewCard(
    standup: StandupDto,
    inFlight: Boolean,
    onSave: (UpdateStandupRequestDto) -> Unit,
) {
    val savedSpec = remember(standup.scheduleCron, standup.timezone) { ScheduleSpec.parse(standup.scheduleCron, standup.timezone) }
    val savedOn = standup.reviewMode == ReviewModeDto.DRAFT_DM
    var on by remember(standup.id, standup.reviewMode) { mutableStateOf(savedOn) }
    var lead by remember(standup.id, standup.reviewLeadMinutes) { mutableStateOf(standup.reviewLeadMinutes) }
    val canReview = ReviewSettings.canReview(savedSpec)
    val dirty = on != savedOn || (on && lead != standup.reviewLeadMinutes)

    SectionCard(
        title = "Review before posting",
        description = "Let people check their own update before it goes to the channel.",
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            Switch(
                checked = on,
                onCheckedChange = { on = it },
                label = "Let people review before it posts",
                ariaLabel = "Let people review before it posts",
                disabled = inFlight || (!canReview && !on),
            )
            if (!canReview) {
                SpanText(
                    text = ReviewSettings.NEEDS_A_DAY,
                    modifier = Modifier.fontSize(Typography.caption.size).color(Palette.warning),
                )
            }
            if (on && canReview) {
                FormRow(
                    label = "Send drafts",
                    hint = ReviewSettings.explanation(savedSpec, lead),
                ) {
                    SegmentedControl(
                        options = ReviewSettings.leadOptions.map { SegmentedOption(it, ReviewSettings.leadLabel(it)) },
                        selected = lead,
                        onSelect = { lead = it },
                        ariaLabel = "How long before posting to send drafts",
                    )
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Reset",
                    variant = ButtonVariant.Ghost,
                    enabled = dirty && !inFlight,
                    onClick = {
                        on = savedOn
                        lead = standup.reviewLeadMinutes
                    },
                )
                Button(
                    label = if (inFlight) "Saving…" else "Save",
                    variant = ButtonVariant.Primary,
                    enabled = dirty && !inFlight && (!on || canReview),
                    onClick = {
                        onSave(
                            UpdateStandupRequestDto(
                                reviewMode = if (on) ReviewModeDto.DRAFT_DM else ReviewModeDto.OFF,
                                reviewLeadMinutes = lead,
                            ),
                        )
                    },
                )
            }
        }
    }
}

@Composable
private fun ActivityWindowCard(
    standup: StandupDto,
    inFlight: Boolean,
    onSave: (UpdateStandupRequestDto) -> Unit,
) {
    var draft by remember(standup.id, standup.maxLookbackDays) {
        mutableStateOf(standup.maxLookbackDays.toString())
    }
    val parsed = draft.trim().toIntOrNull()
    val valid = parsed != null && parsed in 1..30
    val dirty = parsed != null && parsed != standup.maxLookbackDays

    SectionCard(
        title = "Activity window",
        description = "How far back the bot reaches when a prior standup was skipped.",
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            FormRow(
                label = "Lookback cap (days)",
                hint = "Maximum days the bot walks back when the previous standup didn't post " +
                    "(e.g. across weekend + holiday gaps). Default 7.",
            ) {
                Input(
                    type = InputType.Number,
                    attrs = co.maytes.standupbot.dashboard.components.widgets.TextInputStyle.toModifier()
                        .width(120.px)
                        .toAttrs {
                            attr("aria-label", "Lookback cap in days")
                            attr("min", "1")
                            attr("max", "30")
                            attr("step", "1")
                            value(draft)
                            onInput { draft = it.value?.toString().orEmpty() }
                            if (inFlight) attr("disabled", "disabled")
                        },
                )
                if (!valid) {
                    SpanText(
                        text = "Enter a whole number between 1 and 30.",
                        modifier = Modifier
                            .margin(top = Spacing.xxs)
                            .fontSize(Typography.caption.size)
                            .color(Palette.warning),
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Reset",
                    variant = ButtonVariant.Ghost,
                    enabled = dirty && !inFlight,
                    onClick = { draft = standup.maxLookbackDays.toString() },
                )
                Button(
                    label = if (inFlight) "Saving…" else "Save",
                    variant = ButtonVariant.Primary,
                    enabled = valid && dirty && !inFlight,
                    onClick = {
                        parsed?.let { onSave(UpdateStandupRequestDto(maxLookbackDays = it)) }
                    },
                )
            }
        }
    }
}
